import { Polygon as MapPolygon } from '../../../context/context';
import * as turf from '@turf/turf';
import { convexHull } from '../utils/polygon';
import { Feature, FeatureCollection, Polygon, MultiPolygon, Position, GeoJsonProperties } from 'geojson';

// Type definitions for our internal use
type LatLngPair = [number, number];
type TravelTimeCategory = 'good' | 'medium' | 'poor';
type ClassifiedPoints = Record<TravelTimeCategory, LatLngPair[]>;
type TurfPolygonMap = Partial<Record<TravelTimeCategory, Feature<Polygon, GeoJsonProperties>>>;

interface TravelTimeConfigBase {
    color: string;
    borderColor: string;
}

interface TravelTimeConfigWithMinutes extends TravelTimeConfigBase {
    maxMinutes: number;
}

interface TravelTimeConfig {
    good: TravelTimeConfigWithMinutes;
    medium: TravelTimeConfigWithMinutes;
    poor: TravelTimeConfigBase;
}

const TRAVEL_TIME_CONFIG: TravelTimeConfig = {
    good: { maxMinutes: 20, color: '#4caf50', borderColor: '#2e7d32' },
    medium: { maxMinutes: 40, color: '#ffeb3b', borderColor: '#c8b900' },
    poor: { color: '#f44336', borderColor: '#c62828' }
};

// Add this helper function at the top level
function createPolygonFeature(coordinates: Position[][]): Feature<Polygon> {
    return {
        type: 'Feature',
        properties: {},
        geometry: {
            type: 'Polygon',
            coordinates: coordinates
        }
    };
}

/**
 * Classify points by their travel times into good, medium, and poor categories
 */
export function classifyPointsByTravelTime(points: LatLngPair[], travelTimes: number[]): ClassifiedPoints {
    const classified: ClassifiedPoints = { good: [], medium: [], poor: [] };
    
    points.forEach((pt, i) => {
        const time = travelTimes[i];
        if (time < TRAVEL_TIME_CONFIG.good.maxMinutes) {
            classified.good.push(pt);
        } else if (time < TRAVEL_TIME_CONFIG.medium.maxMinutes) {
            classified.medium.push(pt);
        } else {
            classified.poor.push(pt);
        }
    });
    
    return classified;
}

/**
 * Generate simple polygons for each travel time category
 */
export function generateSimplePolygons(classified: ClassifiedPoints): MapPolygon[] {
    const output: MapPolygon[] = [];
    const categories: TravelTimeCategory[] = ['good', 'medium', 'poor'];
    
    // Create a simple polygon for each class with at least 3 points
    for (const category of categories) {
        const points = classified[category];
        if (points.length >= 3) {
            const simplifiedPoints = points.slice(0, Math.min(points.length, 10)); // Take up to 10 points
            const config = TRAVEL_TIME_CONFIG[category];
            
            output.push({
                id: `transitscan-${category}-${Date.now()}`,
                type: 'polygon',
                name: `TransitScan: ${category}`,
                colour: config.color,
                description: category === 'poor' 
                    ? 'Travel time: Over 40 min'
                    : `Travel time: Under ${TRAVEL_TIME_CONFIG[category].maxMinutes} min`,
                opacity: '0.5',
                borderColour: config.borderColor,
                borderWidth: 2,
                isLocked: false,
                coordinates: [simplifiedPoints],
            });
        }
    }
    
    return output;
}

/**
 * Generate better polygons with improved handling for different scales
 */
export function generateBetterPolygons(classified: ClassifiedPoints): MapPolygon[] {
    const output: MapPolygon[] = [];
    const categories: TravelTimeCategory[] = ['good', 'medium', 'poor'];
    
    // Step 1: Create convex hulls and Turf.js polygons for each category
    const hulls: Partial<Record<TravelTimeCategory, LatLngPair[]>> = {};
    const turfPolygons: TurfPolygonMap = {};
    
    for (const category of categories) {
        const points = classified[category];
        if (points && points.length >= 3) {
            // Generate convex hull
            const hull = convexHull(points);
            if (hull.length >= 3) {
                hulls[category] = hull;
                
                // Create a closed ring by adding the first point to the end if needed
                const closedHull = [...hull];
                if (!isRingClosed(closedHull)) {
                    closedHull.push([...closedHull[0]]);
                }
                
                try {
                    // Convert to GeoJSON format for turf.js (lng, lat order)
                    const turfRing = closedHull.map(([lat, lng]) => [lng, lat] as Position);
                    const poly = turf.polygon([turfRing]);
                    
                    // Add buffer and simplification for large areas
                    const bbox = turf.bbox(poly);
                    const diagonalKm = turf.distance(
                        turf.point([bbox[0], bbox[1]]),
                        turf.point([bbox[2], bbox[3]]),
                        { units: 'kilometers' }
                    );
                    
                    if (diagonalKm > 5) {
                        const tolerance = Math.max(0.0001, diagonalKm * 0.00005);
                        const simplified = turf.simplify(poly, { tolerance, highQuality: false });
                        if (simplified) {
                            try {
                                const buffered = turf.buffer(simplified, 0.01, { units: 'kilometers' });
                                if (buffered) {
                                    // Ensure we're working with a Polygon, not a MultiPolygon
                                    if (buffered.geometry.type === 'Polygon') {
                                        turfPolygons[category] = buffered as Feature<Polygon>;
                                    } else if (buffered.geometry.type === 'MultiPolygon') {
                                        // If it's a MultiPolygon, take the largest polygon
                                        const polygonFeatures = buffered.geometry.coordinates.map(coords => 
                                            createPolygonFeature([coords[0]])
                                        );
                                        const areas = polygonFeatures.map(p => turf.area(p));
                                        const largestIndex = areas.indexOf(Math.max(...areas));
                                        turfPolygons[category] = polygonFeatures[largestIndex];
                                    }
                                } else {
                                    turfPolygons[category] = poly;
                                }
                            } catch (e) {
                                console.warn('Failed to buffer polygon:', e);
                                turfPolygons[category] = poly;
                            }
                        } else {
                            turfPolygons[category] = poly;
                        }
                    } else {
                        turfPolygons[category] = poly;
                    }
                } catch (e) {
                    console.error(`Failed to create turf polygon for ${category}:`, e);
                }
            }
        }
    }
    
    // Helper function to create a polygon with consistent styling
    const createPolygon = (
        category: TravelTimeCategory,
        coordinates: LatLngPair[],
        suffix: string = ''
    ): MapPolygon => {
        const config = TRAVEL_TIME_CONFIG[category];
        const name = suffix ? `TransitScan: ${category} (${suffix})` : `TransitScan: ${category}`;
        const description = category === 'poor' 
            ? 'Travel time: Over 40 min'
            : `Travel time: Under ${(config as TravelTimeConfigWithMinutes).maxMinutes} min`;
            
        return {
            id: `transitscan-${category}${suffix ? `-${suffix}` : ''}-${Date.now()}`,
            type: 'polygon',
            name,
            colour: config.color,
            description,
            opacity: suffix ? '0.5' : '0.7',
            borderColour: config.borderColor,
            borderWidth: 2,
            isLocked: false,
            coordinates: [coordinates],
        };
    };
    
    // Add 'good' polygon as-is
    if (hulls.good && hulls.good.length >= 3) {
        output.push(createPolygon('good', hulls.good));
    }
    
    // Process 'medium' with subtraction from 'good'
    if (turfPolygons.medium && turfPolygons.good) {
        try {
            const mediumMinusGood = subtractPolygons(turfPolygons.medium, turfPolygons.good);
            
            if (mediumMinusGood) {
                const coordinates = convertTurfToLatLng(mediumMinusGood);
                if (coordinates) {
                    output.push(createPolygon('medium', coordinates));
                } else if (hulls.medium) {
                    output.push(createPolygon('medium', hulls.medium, 'fallback'));
                }
            }
        } catch (e) {
            console.warn('Failed to process medium polygon:', e);
            if (hulls.medium) {
                output.push(createPolygon('medium', hulls.medium, 'simple'));
            }
        }
    }
    
    // Process 'poor' with subtraction from both 'good' and 'medium'
    if (turfPolygons.poor && (turfPolygons.good || turfPolygons.medium)) {
        try {
            const higherPriorityUnion = unionPolygons(turfPolygons.good, turfPolygons.medium);
            
            if (higherPriorityUnion) {
                const poorMinusHigher = subtractPolygons(turfPolygons.poor, higherPriorityUnion);
                
                if (poorMinusHigher) {
                    const coordinates = convertTurfToLatLng(poorMinusHigher);
                    if (coordinates) {
                        output.push(createPolygon('poor', coordinates));
                    } else if (hulls.poor) {
                        output.push(createPolygon('poor', hulls.poor, 'fallback'));
                    }
                }
            }
        } catch (e) {
            console.warn('Failed to process poor polygon:', e);
            if (hulls.poor) {
                output.push(createPolygon('poor', hulls.poor, 'simple'));
            }
        }
    }
    
    return output;
}

// Helper functions
function isRingClosed(ring: LatLngPair[]): boolean {
    return ring.length >= 4 && 
           ring[0][0] === ring[ring.length - 1][0] && 
           ring[0][1] === ring[ring.length - 1][1];
}

function subtractPolygons(
    minuend: Feature<Polygon>,
    subtrahend: Feature<Polygon>
): Feature<Polygon> | null {
    try {
        // Create a FeatureCollection for the difference operation
        const featureCollection: FeatureCollection<Polygon> = {
            type: 'FeatureCollection',
            features: [minuend, subtrahend]
        };
        
        const result = turf.difference(featureCollection);
        if (!result) return null;
        
        // Handle potential MultiPolygon result
        if (result.geometry.type === 'MultiPolygon') {
            const polygonFeatures = result.geometry.coordinates.map(coords => 
                createPolygonFeature([coords[0]])
            );
            const areas = polygonFeatures.map(p => turf.area(p));
            const largestIndex = areas.indexOf(Math.max(...areas));
            return polygonFeatures[largestIndex];
        }
        return result as Feature<Polygon>;
    } catch (e) {
        console.warn('First attempt at polygon subtraction failed, trying with simplification');
        try {
            const simplified = {
                minuend: turf.simplify(minuend, { tolerance: 0.001 }),
                subtrahend: turf.simplify(subtrahend, { tolerance: 0.001 })
            };

            if (!simplified.minuend || !simplified.subtrahend) {
                return null;
            }

            // Create a FeatureCollection for the simplified difference operation
            const simplifiedFeatureCollection: FeatureCollection<Polygon> = {
                type: 'FeatureCollection',
                features: [simplified.minuend, simplified.subtrahend]
            };

            const result = turf.difference(simplifiedFeatureCollection);
            if (!result) return null;
            
            if (result.geometry.type === 'MultiPolygon') {
                const polygonFeatures = result.geometry.coordinates.map(coords => 
                    createPolygonFeature([coords[0]])
                );
                const areas = polygonFeatures.map(p => turf.area(p));
                const largestIndex = areas.indexOf(Math.max(...areas));
                return polygonFeatures[largestIndex];
            }
            return result as Feature<Polygon>;
        } catch (e2) {
            console.warn('Simplified polygon subtraction also failed');
            return null;
        }
    }
}

function unionPolygons(
    poly1: Feature<Polygon> | null | undefined,
    poly2: Feature<Polygon> | null | undefined
): Feature<Polygon> | null {
    if (!poly1 && !poly2) return null;
    if (!poly1) return poly2 || null;
    if (!poly2) return poly1;
    
    try {
        // Create a FeatureCollection for union operation
        const collection: FeatureCollection<Polygon> = {
            type: 'FeatureCollection',
            features: [poly1, poly2].filter((p): p is Feature<Polygon> => p !== null && p !== undefined)
        };
        
        const result = turf.union(collection);
        if (!result) return null;
        
        // Handle potential MultiPolygon result
        if (result.geometry.type === 'MultiPolygon') {
            const polygonFeatures = result.geometry.coordinates.map(coords => 
                createPolygonFeature([coords[0]])
            );
            const areas = polygonFeatures.map(p => turf.area(p));
            const largestIndex = areas.indexOf(Math.max(...areas));
            return polygonFeatures[largestIndex];
        }
        return result as Feature<Polygon>;
    } catch (e) {
        console.warn('Failed to union polygons:', e);
        // Fallback: return the first polygon
        return poly1;
    }
}

function convertTurfToLatLng(feature: Feature<Polygon>): LatLngPair[] | null {
    if (!feature?.geometry?.coordinates?.[0]?.length) return null;
    
    return feature.geometry.coordinates[0]
        .map(([lng, lat]) => [lat, lng] as LatLngPair);
} 